// frontend-react/src/components/BoxScore/BoxScoreBatting.js
// Batting statistics table for box score display

import React, { useState } from 'react';
import { dynastyTheme } from '../../services/colorService';
import { formatBattingAvg } from '../../services/statFormatUtils';
import { DynastyTable } from '../../services/tableService';
import PlayerProfileModal from '../PlayerProfileModal';
import PlayerLink from '../PlayerLink';

/**
 * BoxScoreBatting Component
 * Displays batting statistics in a table format with team totals
 * 
 * @param {Object[]} hitters - Array of hitter objects with batting stats
 * @param {Object} teamTotals - Team totals for batting stats
 * @param {string} leagueId - League UUID for player modal
 * @param {string} teamId - Team UUID for player modal
 */
const BoxScoreBatting = ({ hitting = [], teamTotals = null, leagueId = null, teamId = null }) => {
  // Player modal state
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle player name click
  const handlePlayerClick = (playerId) => {
    setSelectedPlayerId(playerId);
    setIsModalOpen(true);
  };
  // Helper to extract position from lineup_position (e.g., "C_0" -> "C")
  const extractPosition = (lineupPosition) => {
    if (!lineupPosition) return '-';
    return lineupPosition.split('_')[0];
  };

  // Format OPS from pre-calculated team totals
  const calculateTeamOPS = (hitting) => {
    if (!hitting) return '.000';
    return formatBattingAvg(hitting.ops);
  };

  // Hitting columns for table
  const columns = [
    {
      key: 'position',
      title: 'POS',
      width: 60,
      sortable: true,
      sticky: true,
      stickyLeft: 0,
      sortValue: (player) => extractPosition(player.lineup_position),
      render: (player) => (
        <div className={`text-sm font-semibold ${dynastyTheme.classes.text.warning}`} style={{ fontWeight: '700' }}>
          {extractPosition(player.lineup_position)}
        </div>
      )
    },
    {
      key: 'player_name',
      title: 'Player',
      width: 126,
      sortable: true,
      align: 'left',
      sticky: true,
      stickyLeft: 60,
      sortValue: (player) => player.player_name || '',
      render: (player) => (
        <div className="text-left">
          <PlayerLink mlbId={player.player_id} name={player.player_name} style={{ color: '#f9fafb' }} />
        </div>
      )
    },
    {
      key: 'mlb_team',
      title: 'Team',
      width: 80,
      sortable: true,
      sortValue: (player) => player.mlb_team || '',
      render: (player) => (
        <div className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`}>
          {player.mlb_team || '-'}
        </div>
      )
    },
    {
      key: 'at_bats',
      title: 'AB',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.at_bats || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.at_bats || 0}
        </div>
      )
    },
    {
      key: 'runs',
      title: 'R',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.runs || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.runs || 0}
        </div>
      )
    },
    {
      key: 'hits',
      title: 'H',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.hits || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.hits || 0}
        </div>
      )
    },
    {
      key: 'doubles',
      title: '2B',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.doubles || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.doubles || 0}
        </div>
      )
    },
    {
      key: 'triples',
      title: '3B',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.triples || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.triples || 0}
        </div>
      )
    },
    {
      key: 'home_runs',
      title: 'HR',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.home_runs || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.home_runs || 0}
        </div>
      )
    },
    {
      key: 'rbi',
      title: 'RBI',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.rbi || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.rbi || 0}
        </div>
      )
    },
    {
      key: 'walks',
      title: 'BB',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.walks || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.walks || 0}
        </div>
      )
    },
    {
      key: 'stolen_bases',
      title: 'SB',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.stolen_bases || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.stolen_bases || 0}
        </div>
      )
    },
    {
      key: 'strikeouts',
      title: 'K',
      width: 60,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.strikeouts || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {player.stats?.hitting?.strikeouts || 0}
        </div>
      )
    },
    {
      key: 'batting_avg',
      title: 'AVG',
      width: 80,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.batting_avg || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {formatBattingAvg(player.stats?.hitting?.batting_avg)}
        </div>
      )
    },
    {
      key: 'ops',
      title: 'OPS',
      width: 80,
      sortable: true,
      sortValue: (player) => player.stats?.hitting?.ops || 0,
      render: (player) => (
        <div className={dynastyTheme.classes.text.white}>
          {formatBattingAvg(player.stats?.hitting?.ops)}
        </div>
      )
    }
  ];

  // Prepare data for table (hitters + team totals)
  const tableData = [...hitting];

  if (hitting.length === 0) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
          ⚾ Hitting
        </h3>
        <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
          No hitting stats available for this date
        </div>
      </div>
    );
  }

  return (
    <div className={`${dynastyTheme.components.card.base} p-6`}>
      <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
        ⚾ Hitting
      </h3>
      
      <div 
        className="box-score-batting-wrapper"
        style={{ 
          overflowX: 'auto',
          isolation: 'isolate',
          position: 'relative'
        }}
      >
        <style>{`
          /* Sticky POS column */
          .box-score-batting-wrapper table thead th:first-child,
          .box-score-batting-wrapper table tbody td:first-child {
            position: sticky !important;
            left: 0 !important;
            z-index: 11 !important;
            background-color: ${dynastyTheme.tokens.colors.neutral[800]} !important;
            border-right: none !important;
          }
          .box-score-batting-wrapper table tbody tr td:first-child {
            background-color: inherit !important;
          }
          
          /* Sticky Player column */
          .box-score-batting-wrapper table thead th:nth-child(2),
          .box-score-batting-wrapper table tbody td:nth-child(2) {
            position: sticky !important;
            left: 60px !important;
            z-index: 10 !important;
            background-color: ${dynastyTheme.tokens.colors.neutral[800]} !important;
            box-shadow: 2px 0 4px rgba(0, 0, 0, 0.3) !important;
          }
          .box-score-batting-wrapper table tbody tr td:nth-child(2) {
            background-color: inherit !important;
          }
        `}</style>
        <DynastyTable
          data={tableData}
          columns={columns}
          maxHeight="600px"
        />
      </div>

      {/* Team Totals Row */}
      {teamTotals && teamTotals.hitting && (
        <div className={`mt-4 p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border-t-2 ${dynastyTheme.classes.border.primary}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                AB
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {teamTotals.hitting.at_bats || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                R
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {teamTotals.hitting.runs || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                H
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {teamTotals.hitting.hits || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                HR
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {teamTotals.hitting.home_runs || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                RBI
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {teamTotals.hitting.rbi || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                SB
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {teamTotals.hitting.stolen_bases || 0}
              </div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                AVG
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {formatBattingAvg(teamTotals.hitting.avg)}
              </div>
            </div>
            <div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                OPS
              </div>
              <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                {calculateTeamOPS(teamTotals.hitting)}
              </div>
            </div>
          </div>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-2 text-center`}>
            Team Totals
          </div>
        </div>
      )}

      {/* Player Profile Modal */}
      <PlayerProfileModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userTeamId={teamId}
      />
    </div>
  );
};

export default BoxScoreBatting;
